import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Bell, 
  AlertTriangle, 
  CloudSnow, 
  Car,
  X,
  ChevronRight,
  Shield,
  Clock,
  CheckCircle,
  Phone,
  MapPin,
  Check,
  XCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NotificationCenter() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [alerts, setAlerts] = useState({ weather: [], roads: [] });
  const [overrideRequests, setOverrideRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  
  const isManager = user?.role === 'admin' || user?.role === 'manager';

  useEffect(() => {
    if (isManager) {
      fetchAlerts();
      fetchOverrideRequests();
      // Refresh alerts every minute for override requests, 5 minutes for weather
      const overrideInterval = setInterval(fetchOverrideRequests, 60 * 1000);
      const alertsInterval = setInterval(fetchAlerts, 5 * 60 * 1000);
      return () => {
        clearInterval(overrideInterval);
        clearInterval(alertsInterval);
      };
    }
  }, [isManager]);

  // Show toast notification when critical alerts are first detected
  useEffect(() => {
    const weatherCritical = alerts.weather.length;
    const roadsCritical = alerts.roads.filter(r => r.severity === 'critical').length;
    
    if (weatherCritical > 0 || roadsCritical > 0) {
      const totalCritical = weatherCritical + roadsCritical;
      // Only show toast once per session using sessionStorage
      const alertShown = sessionStorage.getItem('alertToastShown');
      if (!alertShown) {
        toast.warning(
          `${totalCritical} critical alert${totalCritical > 1 ? 's' : ''} require your attention`,
          {
            description: 'Click the bell icon to review operations alerts',
            duration: 8000,
          }
        );
        sessionStorage.setItem('alertToastShown', 'true');
      }
    }
  }, [alerts]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/notifications/alerts`);
      setAlerts({
        weather: response.data.weather || [],
        roads: response.data.roads?.slice(0, 5) || [] // Limit to top 5
      });
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
      // Fallback: try individual endpoints (BC roads - 98% of operations)
      try {
        const [weatherRes, roadsRes] = await Promise.all([
          axios.get(`${API}/weather`),
          axios.get(`${API}/roads/bc/conditions`)
        ]);

        const criticalWeather = weatherRes.data.weather.filter(
          w => w.risk.override_required && !w.risk.override_active
        );

        // BC roads have different structure - extract from highways array
        const bcHighways = roadsRes.data.highways || [];
        const criticalRoads = bcHighways.filter(
          h => h.severity === 'critical' || h.severity === 'warning'
        ).map(h => ({
          ...h,
          name: h.highway,
          condition_label: h.condition,
          source: 'DriveBC'
        }));

        setAlerts({
          weather: criticalWeather,
          roads: criticalRoads.slice(0, 5)
        });
      } catch (fallbackError) {
        console.error('Fallback fetch also failed:', fallbackError);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchOverrideRequests = async () => {
    try {
      const response = await axios.get(`${API}/override-requests?status=pending`);
      setOverrideRequests(response.data.requests || []);
      
      // Show toast for new override requests
      if (response.data.requests?.length > 0) {
        const lastShown = sessionStorage.getItem('lastOverrideCount');
        if (!lastShown || parseInt(lastShown) < response.data.requests.length) {
          toast.warning(
            `${response.data.requests.length} driver override request(s) pending`,
            { description: 'Click to review and respond', duration: 10000 }
          );
          sessionStorage.setItem('lastOverrideCount', response.data.requests.length.toString());
        }
      }
    } catch (error) {
      console.error('Failed to fetch override requests:', error);
    }
  };

  const handleOverrideResponse = async (requestId, action) => {
    try {
      await axios.post(`${API}/override-requests/${requestId}/respond?action=${action}`);
      toast.success(`Override ${action === 'approve' ? 'approved' : 'denied'}`);
      fetchOverrideRequests();
    } catch (error) {
      toast.error('Failed to respond to override request');
    }
  };

  const totalAlerts = alerts.weather.length + alerts.roads.length + overrideRequests.length;

  const handleViewWeather = () => {
    setIsOpen(false);
    navigate('/dashboard/weather');
  };

  const handleQuickOverride = async (location) => {
    try {
      await axios.post(`${API}/weather/override`, {
        location_id: location.location.id,
        reason: 'Quick approval from notification center',
        duration_hours: 4
      });
      toast.success(`Override approved for ${location.location.name}`);
      fetchAlerts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create override');
    }
  };

  if (!isManager) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Notification Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg hover:bg-secondary transition-colors"
        data-testid="notification-bell"
      >
        <Bell className={`w-5 h-5 ${totalAlerts > 0 ? 'text-primary' : 'text-muted-foreground'}`} />
        {totalAlerts > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
            {totalAlerts > 9 ? '9+' : totalAlerts}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-card border border-border rounded-xl shadow-xl z-50 overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 border-b border-border bg-secondary/30">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-foreground flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-primary" />
                Operations Alerts
              </h3>
              <button 
                onClick={() => setIsOpen(false)}
                className="p-1 rounded hover:bg-secondary"
              >
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>
            {totalAlerts > 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                {totalAlerts} alert{totalAlerts !== 1 ? 's' : ''} requiring attention
              </p>
            )}
          </div>

          {/* Content */}
          <div className="max-h-[400px] overflow-y-auto">
            {loading ? (
              <div className="p-8 flex items-center justify-center">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : totalAlerts === 0 ? (
              <div className="p-8 text-center">
                <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
                <p className="text-sm font-medium text-foreground">All Clear</p>
                <p className="text-xs text-muted-foreground mt-1">
                  No critical conditions requiring attention
                </p>
              </div>
            ) : (
              <>
                {/* Driver Override Requests - PRIORITY */}
                {overrideRequests.length > 0 && (
                  <div className="p-3 bg-orange-500/5">
                    <h4 className="text-xs font-semibold text-orange-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                      <Phone className="w-3 h-3" />
                      🚨 Driver Override Requests ({overrideRequests.length})
                    </h4>
                    <div className="space-y-2">
                      {overrideRequests.map((request) => (
                        <div 
                          key={request.id}
                          className="p-3 rounded-lg bg-orange-500/10 border border-orange-500/40"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <p className="font-medium text-foreground text-sm flex items-center gap-1">
                                <MapPin className="w-3 h-3 text-orange-400" />
                                {request.route_from} → {request.route_to}
                              </p>
                              <p className="text-xs text-orange-400 mt-0.5">
                                Driver: {request.driver_name || request.driver_phone}
                              </p>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {request.reason}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              className="flex-1 h-7 text-xs bg-emerald-600 hover:bg-emerald-700"
                              onClick={() => handleOverrideResponse(request.id, 'approve')}
                            >
                              <Check className="w-3 h-3 mr-1" />
                              Approve
                            </Button>
                            <Button 
                              size="sm" 
                              variant="destructive"
                              className="flex-1 h-7 text-xs"
                              onClick={() => handleOverrideResponse(request.id, 'deny')}
                            >
                              <XCircle className="w-3 h-3 mr-1" />
                              Deny
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Critical Weather Alerts */}
                {alerts.weather.length > 0 && (
                  <div className="p-3">
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-2">
                      <CloudSnow className="w-3 h-3" />
                      Critical Weather ({alerts.weather.length})
                    </h4>
                    <div className="space-y-2">
                      {alerts.weather.map((item) => (
                        <div 
                          key={item.location.id}
                          className="p-3 rounded-lg bg-red-500/10 border border-red-500/30"
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium text-foreground text-sm">
                                {item.location.name}
                              </p>
                              <p className="text-xs text-red-400 mt-0.5">
                                {Math.round(item.weather.temp)}°C - {item.risk.message}
                              </p>
                            </div>
                            <Badge variant="outline" className="bg-red-500/10 text-red-400 border-red-500/30 text-xs">
                              CRITICAL
                            </Badge>
                          </div>
                          <div className="mt-2 flex gap-2">
                            <Button 
                              size="sm" 
                              variant="destructive"
                              className="flex-1 h-7 text-xs"
                              onClick={() => handleQuickOverride(item)}
                            >
                              <Shield className="w-3 h-3 mr-1" />
                              Quick Approve (4h)
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="h-7 text-xs"
                              onClick={handleViewWeather}
                            >
                              Details
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Road Alerts - BC (DriveBC) */}
                {alerts.roads.length > 0 && (
                  <div className="p-3 border-t border-border">
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-2">
                      <Car className="w-3 h-3" />
                      BC Road Warnings ({alerts.roads.length})
                    </h4>
                    <div className="space-y-2">
                      {alerts.roads.map((item, idx) => {
                        const highway = item.highway || item;
                        const highwayName = highway.name || item.name || 'Unknown Highway';
                        const isCritical = item.severity === 'critical';
                        const conditionLabel = item.condition_label || item.condition || 'Unknown';
                        const eventCount = item.event_count || item.segment_count || 0;
                        return (
                          <div 
                            key={highway.id || highway.pattern || idx}
                            className={`p-3 rounded-lg ${
                              isCritical 
                                ? 'bg-red-500/10 border border-red-500/30' 
                                : 'bg-orange-500/10 border border-orange-500/30'
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div>
                                <p className="font-medium text-foreground text-sm">
                                  {highwayName}
                                </p>
                                <p className={`text-xs mt-0.5 ${isCritical ? 'text-red-400' : 'text-orange-400'}`}>
                                  {conditionLabel} - {eventCount} event{eventCount !== 1 ? 's' : ''}
                                </p>
                                {item.source && (
                                  <p className="text-xs text-muted-foreground mt-0.5">
                                    Source: {item.source}
                                  </p>
                                )}
                              </div>
                              <Badge 
                                variant="outline" 
                                className={`text-xs ${
                                  isCritical 
                                    ? 'bg-red-500/10 text-red-400 border-red-500/30' 
                                    : 'bg-orange-500/10 text-orange-400 border-orange-500/30'
                                }`}
                              >
                                {isCritical ? 'MAJOR' : 'MODERATE'}
                              </Badge>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 border-t border-border bg-secondary/30">
            <Button 
              variant="outline" 
              className="w-full justify-center gap-2"
              onClick={handleViewWeather}
            >
              View All Conditions
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
